import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
  WarningCircleIcon,
} from '@noor/ui';
import { Can } from '@/entities/access';
import { chatOwnerName, type Chat } from '@/entities/conversation';
import { usePendingOfferStore } from '../model/pending-offer-store';
import { useConfirmMove } from '../model/use-chat-mutations';

interface ChatOfferDialogProps {
  /** Open an accepted chat (the page pushes it into the URL). */
  onAccept: (chat: Chat) => void;
}

/**
 * "Do you want to take this chat?" — the hand-over prompt an operator gets when
 * another operator transfers a chat to them (cpanel `OperatorAccept`). Open
 * while a `new_chat` push carrying a `new_user_id` is pending.
 *
 * Accept confirms the move and opens the chat; reject declines it. (cpanel also
 * jumped to the chat after a REJECT because both shared one `onSuccess`; that
 * is dropped — you should not land in a chat you just declined.)
 */
export function ChatOfferDialog({ onAccept }: ChatOfferDialogProps) {
  const { t } = useTranslation('inbox');
  const offer = usePendingOfferStore((state) => state.offer);
  const confirmMove = useConfirmMove();

  const open = Boolean(offer?.new_user_id);
  const name = chatOwnerName(offer?.user, t('offer.untitled'));

  return (
    <Can permission="inbox.reply">
      <Dialog open={open}>
        <DialogContent
          showClose={false}
          className="max-w-[calc(100%-2rem)] gap-8 rounded-3xl p-6 sm:max-w-[30rem]"
        >
          <div className="flex w-full flex-col items-center gap-6 px-[42px]">
            <WarningCircleIcon className="size-[72px] shrink-0" />
            <div className="flex w-full flex-col items-center gap-2 text-center">
              <DialogTitle className="text-[22px] leading-tight font-semibold tracking-[-0.88px]">
                {t('offer.title')}
              </DialogTitle>
              <DialogDescription className="text-sm leading-snug font-medium text-muted-foreground">
                {offer?.user ? t('offer.description', { name }) : t('offer.noData')}
              </DialogDescription>
            </div>
          </div>
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="soft"
              shape="rounded"
              size="modal"
              className="flex-1"
              disabled={confirmMove.isPending || !offer}
              onClick={() => offer && confirmMove.mutate({ chat: offer, confirm: false })}
            >
              {t('offer.reject')}
            </Button>
            <Button
              type="button"
              variant="dark"
              shape="rounded"
              size="modal"
              className="flex-1"
              disabled={confirmMove.isPending || !offer}
              aria-busy={confirmMove.isPending}
              onClick={() =>
                offer &&
                confirmMove.mutate(
                  { chat: offer, confirm: true },
                  { onSuccess: () => onAccept(offer) },
                )
              }
            >
              {confirmMove.isPending ? (
                <Spinner size="sm" className="text-current" />
              ) : null}
              {t('offer.accept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Can>
  );
}
